import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import './AssessmentPage.css';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import InstructionsPanel from './InstructionsPanel';
import AudioRecorder from './AudioRecorder';
import RealtimeCaption from './RealtimeCaption';
import { useRealtimeSTT } from '../hooks/useRealtimeSTT';
import { useSidebar } from '../contexts/SidebarContext';
import { publicStructuralSegmentsAPI, recordingsAPI, StructuralSegment, SegmentAudio, authAPI, subscriptionAPI, dialogueCategoriesAPI, CategoryDialogue, DialoguePartAudio, autoplayChimeAPI, mockCreditsAPI } from '../services/api';
import { getOrCreateMockDialogueLabel } from '../utils/mockDialogueCodes';
import { dialogueCategoriesListPath, parseCategoryQueryId } from '../utils/dialogueCategoriesNav';
import { getAuthenticatedAudioStreamUrl } from '../config/api';
import { FiCheckCircle, FiX, FiAlertCircle, FiPlay, FiPause } from 'react-icons/fi';
import SystemPlaybackSpeedControl, { applySystemPlaybackRate, useSystemPlaybackRate } from './SystemPlaybackSpeedControl';

interface AssessmentPageProps {
  onLogout?: () => void;
}

type AssessmentState = 'loading' | 'ready' | 'playing' | 'recording' | 'playback' | 'completed' | 'submitting' | 'error';

/** Per-clip review dialog: collapsible reference text, caption, and stepped expected translations. */
type ReviewRowExpandState = {
  showSourceText: boolean;
  showCaption: boolean;
  /**
   * Which expected translation is shown (1-based). 0 = hidden.
   * "More idea" / "Previous idea" step through alternatives one at a time.
   */
  expectedRevealed: number;
};

const DEFAULT_REVIEW_EXPAND: ReviewRowExpandState = {
  showSourceText: false,
  showCaption: false,
  expectedRevealed: 0,
};

/**
 * Coerce API payloads into up to 3 distinct expected lines for review.
 * Handles duplicate-padded lists, a single field with 2–3 newline-separated alternatives, or a JSON string.
 */
function normalizeExpectedTranslations(raw: unknown): string[] {
  const out: string[] = [];
  const pushUnique = (s: string) => {
    const t = s.trim();
    if (!t || out.includes(t)) return;
    out.push(t);
  };

  const consumeString = (s: string) => {
    const t = s.trim();
    if (!t) return;
    if (t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) {
          for (const item of parsed) {
            if (typeof item === 'string') pushUnique(item);
            else if (item != null) pushUnique(String(item));
          }
          return;
        }
      } catch {
        /* fall through */
      }
    }
    pushUnique(t);
  };

  if (Array.isArray(raw) && raw.length === 1 && typeof raw[0] === 'string') {
    const blob = raw[0].trim();
    const lines = blob.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    if (lines.length >= 2 && lines.length <= 3) {
      lines.forEach(pushUnique);
      return out.slice(0, 3);
    }
  }

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') consumeString(item);
      else if (item != null) consumeString(String(item));
    }
  } else if (typeof raw === 'string') {
    consumeString(raw);
  }
  return out.slice(0, 3);
}

const AUTOPLAY_INTRO_NEVER_KEY = 'naati_autoplay_intro_never';
/**
 * Ms after chime `ended` before flipping to recording state.
 * Most perceived gap after chime is from `getUserMedia` unless mic prewarm is active (see AudioRecorder `prewarmMic`).
 */
const RECORDING_PAUSE_AFTER_CHIME_END_MS = 0;
/** If `ended` never fires (bad file / browser quirk), still proceed so the user is not stuck. */
const CHIME_PLAYBACK_SAFETY_TIMEOUT_MS = 20_000;
/** Retries when `play()` fails (mic prewarm race, transient autoplay quirks). */
const CHIME_PLAY_MAX_ATTEMPTS = 5;
const CHIME_PLAY_RETRY_BASE_MS = 60;

function getBriefingFileIdForPart(categoryDialogue: CategoryDialogue | null, partId: number): number | undefined {
  if (!categoryDialogue?.parts?.length) return undefined;
  const p = categoryDialogue.parts.find((x) => x.id === partId);
  return p?.briefing_audio_detail?.id ?? p?.briefing_audio ?? undefined;
}

function isFirstClipOfPart(sortedAudios: DialoguePartAudio[], clipIndex: number): boolean {
  if (clipIndex < 0 || clipIndex >= sortedAudios.length) return false;
  const pid = sortedAudios[clipIndex].part;
  if (clipIndex === 0) return true;
  return sortedAudios[clipIndex - 1].part !== pid;
}

function needsBriefingBeforeClip(
  categoryDialogue: CategoryDialogue | null,
  sortedAudios: DialoguePartAudio[],
  clipIndex: number,
  played: Set<number>
): boolean {
  if (!categoryDialogue || !sortedAudios.length || clipIndex < 0 || clipIndex >= sortedAudios.length) return false;
  const pid = sortedAudios[clipIndex]?.part;
  if (pid == null) return false;
  if (!isFirstClipOfPart(sortedAudios, clipIndex)) return false;
  if (played.has(pid)) return false;
  return getBriefingFileIdForPart(categoryDialogue, pid) != null;
}

const AssessmentPage: React.FC<AssessmentPageProps> = ({ onLogout }) => {
  const { segmentId, dialogueId } = useParams<{ segmentId?: string; dialogueId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  /**
   * Mock Service flow: `?mock=1` survives refresh; `location.state.fromMockService` covers same-session navigation.
   * Dialogue Categories practice never sets the query or state flag.
   */
  const isMockServiceFlow = Boolean(
    searchParams.get('mock') === '1' ||
      (location.state as { fromMockService?: boolean } | null)?.fromMockService
  );
  const { isCollapsed, setIsCollapsed } = useSidebar();

  const goBackFromAssessmentError = useCallback(() => {
    if (isMockServiceFlow && dialogueId) {
      navigate('/mock-service');
      return;
    }
    if (segmentId) {
      navigate('/material/structured-segments');
    } else if (dialogueId) {
      const fromState = (location.state as { categoryId?: number } | null)?.categoryId;
      navigate(
        dialogueCategoriesListPath(
          fromState ?? parseCategoryQueryId(searchParams.get('category'))
        )
      );
    }
  }, [isMockServiceFlow, dialogueId, segmentId, navigate, location.state, searchParams]);
  
  const [segment, setSegment] = useState<StructuralSegment | null>(null);
  const [categoryDialogue, setCategoryDialogue] = useState<CategoryDialogue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
    is_staff: false
  });
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [mockAccessReady, setMockAccessReady] = useState<boolean | null>(null);
  
  // Assessment state
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [state, setState] = useState<AssessmentState>('loading');
  const [recordings, setRecordings] = useState<Map<number, Blob>>(new Map()); // audio index -> blob
  const [recordingDurations, setRecordingDurations] = useState<Map<number, number>>(new Map()); // audio index -> duration in seconds
  const [transcripts, setTranscripts] = useState<Map<number, string>>(new Map()); // audio index -> STT transcript
  /** Per take: true = live caption was ON when recording finished; false = user had caption off (or mock). */
  const [captionOnForRecording, setCaptionOnForRecording] = useState<Map<number, boolean>>(new Map());
  const [attempts, setAttempts] = useState<Map<number, number>>(new Map()); // audio index -> attempts count
  const attemptsRef = useRef<Map<number, number>>(new Map()); // Ref for attempts to avoid closure issues
  const [attemptsUpdateTrigger, setAttemptsUpdateTrigger] = useState(0); // Trigger for re-render
  const [systemAudioURL, setSystemAudioURL] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  
  // States for review dialog
  const [playingReviewRecordingIndex, setPlayingReviewRecordingIndex] = useState<number | null>(null);
  const [reviewedRecordings, setReviewedRecordings] = useState<Set<number>>(new Set());
  const reviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [reviewAudioURL, setReviewAudioURL] = useState<string | null>(null);
  const [reviewRowExpand, setReviewRowExpand] = useState<Map<number, ReviewRowExpandState>>(() => new Map());
  const [systemPlaybackRate, setSystemPlaybackRate] = useSystemPlaybackRate();
  
  // Captioning: hidden only on Mock Service flow, not when practising the same dialogue from Dialogue Categories.
  const [captionEnabled, setCaptionEnabled] = useState(true);
  // Autoplay defaults on (mock forces on via effectiveAutoplayEnabled).
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [showAutoplayIntroModal, setShowAutoplayIntroModal] = useState(false);
  const [autoplayIntroNeverAgain, setAutoplayIntroNeverAgain] = useState(false);
  /** Blob URL for per-dialogue briefing (not a scored take — no recording). */
  const [briefingBlobUrl, setBriefingBlobUrl] = useState<string | null>(null);
  /** Part IDs whose briefing has finished this session (before first clip of that block). */
  const [playedPartBriefings, setPlayedPartBriefings] = useState<Set<number>>(() => new Set());

  const playingBriefingRef = useRef(false);
  const briefingTargetPartIdRef = useRef<number | null>(null);
  const playDialogueImmediatelyAfterBriefingRef = useRef(false);
  const shouldAutoplayNextSystemClipRef = useRef(false);
  const skipAutoAdvanceRef = useRef(false);
  const pendingPlayAfterIndexChangeRef = useRef(false);
  const pendingPreviousAfterStopRef = useRef(false);
  const userSessionStartedRef = useRef(false);
  const autoplayPreChimeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayChimeAudioRef = useRef<HTMLAudioElement | null>(null);
  /** Preloaded chime element — reused across clips; never clear `src` between plays. */
  const chimeAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const chimeBlobSrcRef = useRef<string | null>(null);
  const chimePlaybackGenRef = useRef(0);
  /** True after “I understand” this session, or from localStorage “never show again”. */
  const autoplayBehaviorAcknowledgedRef = useRef(false);
  const deferFirstDialogueAfterBriefingRef = useRef(false);
  /** Set when a system clip ended with autoplay but intro not yet acknowledged — chime waits for I understand. */
  const pendingPostSystemAutoplayChimeRef = useRef(false);
  const autoplayIntroScheduledRef = useRef(false);
  const canAutoStartRecordingRef = useRef(false);
  /** Live mirror of effectiveAutoplayEnabled for callbacks that don't re-close over it. */
  const effectiveAutoplayEnabledRef = useRef(false);
  const [autoplayChimeAudioId, setAutoplayChimeAudioId] = useState<number | null>(null);
  const [chimeBlobUrl, setChimeBlobUrl] = useState<string | null>(null);
  const chimeBlobUrlRef = useRef<string | null>(null);
  const [micPrewarmActive, setMicPrewarmActive] = useState(false);
  const [micPrewarmRevokeTick, setMicPrewarmRevokeTick] = useState(0);

  // Realtime Speech-to-Text caption
  const {
    transcript,
    partialText,
    error: captionError,
    start: startCaption,
    stop: stopCaption,
    reset: resetCaption
  } = useRealtimeSTT();

  // Refs for cleanup and race condition prevention
  const fetchAbortControllerRef = useRef<AbortController | null>(null);
  const currentAudioIdRef = useRef<number | null>(null);
  const recordingsRef = useRef<Map<number, Blob>>(new Map()); // Ref to track recordings for closure access
  const transcriptRef = useRef<string>('');
  useEffect(() => {
    transcriptRef.current = [...transcript.map((s: { text: string }) => s.text), partialText].filter(Boolean).join(' ');
  }, [transcript, partialText]);
  
  // Collapse sidebar by default when component mounts
  useEffect(() => {
    setIsCollapsed(true);
  }, [setIsCollapsed]);

  useEffect(() => {
    chimeBlobUrlRef.current = chimeBlobUrl;
  }, [chimeBlobUrl]);

  const pauseActiveChimePlayback = useCallback((clearActiveRef = true) => {
    const ch = autoplayChimeAudioRef.current ?? chimeAudioElementRef.current;
    if (ch) {
      try {
        ch.pause();
        ch.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    if (clearActiveRef) {
      autoplayChimeAudioRef.current = null;
    }
  }, []);

  const destroyChimeAudioElement = useCallback(() => {
    pauseActiveChimePlayback();
    const ch = chimeAudioElementRef.current;
    if (ch) {
      try {
        ch.pause();
        ch.removeAttribute('src');
        ch.load();
      } catch {
        /* ignore */
      }
      chimeAudioElementRef.current = null;
      chimeBlobSrcRef.current = null;
    }
  }, [pauseActiveChimePlayback]);

  useEffect(() => {
    if (!chimeBlobUrl) {
      destroyChimeAudioElement();
      return undefined;
    }

    let audio = chimeAudioElementRef.current;
    if (!audio) {
      audio = new Audio();
      audio.preload = 'auto';
      chimeAudioElementRef.current = audio;
    }

    if (chimeBlobSrcRef.current !== chimeBlobUrl) {
      audio.src = chimeBlobUrl;
      chimeBlobSrcRef.current = chimeBlobUrl;
      audio.load();
    }

    return undefined;
  }, [chimeBlobUrl, destroyChimeAudioElement]);

  useEffect(() => {
    if (state === 'recording') {
      setMicPrewarmActive(false);
    }
  }, [state]);

  useEffect(() => {
    if (!showAutoplayIntroModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showAutoplayIntroModal]);

  const cancelAutoplayRecordingSchedule = useCallback(() => {
    setMicPrewarmActive(false);
    setMicPrewarmRevokeTick((t) => t + 1);
    chimePlaybackGenRef.current += 1;
    if (autoplayPreChimeTimerRef.current) {
      clearTimeout(autoplayPreChimeTimerRef.current);
      autoplayPreChimeTimerRef.current = null;
    }
    pauseActiveChimePlayback();
  }, [pauseActiveChimePlayback]);

  useEffect(() => {
    try {
      autoplayBehaviorAcknowledgedRef.current =
        localStorage.getItem(AUTOPLAY_INTRO_NEVER_KEY) === '1';
    } catch {
      autoplayBehaviorAcknowledgedRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    autoplayChimeAPI
      .get()
      .then((res) => {
        if (!cancelled) {
          setAutoplayChimeAudioId(res.autoplay_chime_audio_id ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setAutoplayChimeAudioId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!autoplayChimeAudioId) {
      setChimeBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return undefined;
    }
    let alive = true;
    recordingsAPI
      .fetchAudioBlob(autoplayChimeAudioId)
      .then((url) => {
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        setChimeBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch(() => {
        if (alive) {
          setChimeBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [autoplayChimeAudioId]);

  // Flat dialogue audios (typed) — used for briefing / ordering alongside flat `sortedAudios`.
  const sortedDialogueAudiosFlat = useMemo((): DialoguePartAudio[] => {
    if (!categoryDialogue?.audios) return [];
    return [...categoryDialogue.audios].sort((a, b) => a.order - b.order);
  }, [categoryDialogue]);

  // Get sorted audios - support both segment and category dialogue (memoized: stable ref for effects)
  const sortedAudios = useMemo(() => {
    if (segment?.audios) {
      return [...segment.audios].sort((a: SegmentAudio, b: SegmentAudio) => a.order - b.order);
    }
    return sortedDialogueAudiosFlat;
  }, [segment, sortedDialogueAudiosFlat]);

  const totalAudios = sortedAudios.length;
  const currentAudio = sortedAudios[currentAudioIndex];
  const dialogueIdNum = dialogueId ? Number(dialogueId) : NaN;
  const dialogueMatchesRoute = Boolean(
    categoryDialogue && dialogueId && !Number.isNaN(dialogueIdNum) && categoryDialogue.id === dialogueIdNum
  );
  const isDialogueCategoryFlow = Boolean(dialogueId && dialogueMatchesRoute);
  const segmentIdNumForRoute = segmentId ? Number(segmentId) : NaN;
  const segmentMatchesRoute = Boolean(
    segment && segmentId && !Number.isNaN(segmentIdNumForRoute) && segment.id === segmentIdNumForRoute
  );
  const isStructuralSegmentFlow = Boolean(segmentId && segmentMatchesRoute);
  /** Mock Service submit: audio is stored for grading. Segment / dialogue category practice uses registerPracticeSession only. */
  const isMockTestSubmission =
    isMockServiceFlow && isDialogueCategoryFlow && Boolean(categoryDialogue?.enable_as_mock);
  /** Mock: autoplay (incl. chime) is always on and not shown in UI. */
  const effectiveAutoplayEnabled = isMockServiceFlow || autoplayEnabled;
  effectiveAutoplayEnabledRef.current = effectiveAutoplayEnabled;

  useEffect(() => {
    if (!effectiveAutoplayEnabled) {
      cancelAutoplayRecordingSchedule();
    }
  }, [effectiveAutoplayEnabled, cancelAutoplayRecordingSchedule]);

  const dialogueFirstClipNeedsBriefing = useMemo(
    () =>
      Boolean(
        isDialogueCategoryFlow &&
          categoryDialogue &&
          needsBriefingBeforeClip(
            categoryDialogue,
            sortedDialogueAudiosFlat,
            0,
            playedPartBriefings
          )
      ),
    [isDialogueCategoryFlow, categoryDialogue, sortedDialogueAudiosFlat, playedPartBriefings]
  );

  useEffect(() => {
    if (!effectiveAutoplayEnabled || loading || state !== 'ready') return;
    if (autoplayBehaviorAcknowledgedRef.current) return;
    if (autoplayIntroScheduledRef.current) return;
    if (showAutoplayIntroModal) return;
    if (!isStructuralSegmentFlow && !(isDialogueCategoryFlow && categoryDialogue)) return;

    if (isStructuralSegmentFlow) {
      autoplayIntroScheduledRef.current = true;
      setShowAutoplayIntroModal(true);
      return;
    }

    if (isDialogueCategoryFlow && !dialogueFirstClipNeedsBriefing) {
      autoplayIntroScheduledRef.current = true;
      setShowAutoplayIntroModal(true);
    }
  }, [
    effectiveAutoplayEnabled,
    loading,
    state,
    isStructuralSegmentFlow,
    isDialogueCategoryFlow,
    categoryDialogue,
    dialogueFirstClipNeedsBriefing,
    showAutoplayIntroModal,
  ]);

  useEffect(() => {
    if (!effectiveAutoplayEnabled && showAutoplayIntroModal) {
      if (deferFirstDialogueAfterBriefingRef.current) {
        deferFirstDialogueAfterBriefingRef.current = false;
        playDialogueImmediatelyAfterBriefingRef.current = true;
        requestAnimationFrame(() => {
          setState((s) => (s === 'ready' ? 'playing' : s));
        });
      }
      pendingPostSystemAutoplayChimeRef.current = false;
      autoplayIntroScheduledRef.current = false;
      setShowAutoplayIntroModal(false);
    }
  }, [effectiveAutoplayEnabled, showAutoplayIntroModal]);

  const needsBriefingNow =
    dialogueMatchesRoute &&
    !!categoryDialogue &&
    needsBriefingBeforeClip(categoryDialogue, sortedDialogueAudiosFlat, currentAudioIndex, playedPartBriefings);
  const partIdForBriefing = sortedDialogueAudiosFlat[currentAudioIndex]?.part;
  const currentBriefingAudioId =
    needsBriefingNow && partIdForBriefing != null
      ? getBriefingFileIdForPart(categoryDialogue, partIdForBriefing)
      : undefined;
  const inBriefingPlaybackPhase = Boolean(
    isDialogueCategoryFlow && currentBriefingAudioId && briefingBlobUrl
  );
  const effectiveSystemAudioURL = inBriefingPlaybackPhase ? briefingBlobUrl : systemAudioURL || undefined;

  useEffect(() => {
    canAutoStartRecordingRef.current =
      state === 'ready' && !!systemAudioURL && !inBriefingPlaybackPhase;
  }, [state, systemAudioURL, inBriefingPlaybackPhase]);

  useEffect(() => {
    if (!dialogueMatchesRoute || !currentBriefingAudioId) {
      setBriefingBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      if (!dialogueMatchesRoute) {
        setPlayedPartBriefings(new Set());
      }
      return undefined;
    }

    let alive = true;

    recordingsAPI
      .fetchAudioBlob(currentBriefingAudioId)
      .then((url) => {
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        setBriefingBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch((err) => {
        console.error('[AssessmentPage] Failed to load dialogue briefing:', err);
      });

    return () => {
      alive = false;
    };
  }, [dialogueMatchesRoute, currentBriefingAudioId, categoryDialogue?.id]);

  useEffect(() => {
    return () => {
      chimePlaybackGenRef.current += 1;
      if (autoplayPreChimeTimerRef.current) {
        clearTimeout(autoplayPreChimeTimerRef.current);
        autoplayPreChimeTimerRef.current = null;
      }
      pauseActiveChimePlayback();
      destroyChimeAudioElement();
    };
  }, [pauseActiveChimePlayback, destroyChimeAudioElement]);

  // Fixed: Check if all recordings are complete and we've finished the last one
  // Use both state and ref to ensure we have the latest value
  const allRecordingsComplete = totalAudios > 0 && 
    recordings.size === totalAudios && 
    recordingsRef.current.size === totalAudios &&
    (currentAudioIndex >= totalAudios - 1 && recordings.has(totalAudios - 1) && recordingsRef.current.has(totalAudios - 1));

  useEffect(() => {
    fetchUserProfile();
    if (isMockServiceFlow) {
      checkMockAccess();
    } else {
      checkSubscription();
    }
  }, [isMockServiceFlow]);

  useEffect(() => {
    // Validate segmentId or dialogueId before processing
    if (!segmentId && !dialogueId) {
      setError('Segment ID or dialogue ID is required');
      setLoading(false);
      return;
    }

    // Validate segmentId or dialogueId is a valid number
    if (segmentId) {
      const segmentIdNum = Number(segmentId);
      if (isNaN(segmentIdNum) || segmentIdNum <= 0) {
        setError('Invalid segment ID');
        setLoading(false);
        setState('error');
        return;
      }
    }

    if (dialogueId) {
      const dialogueIdNum = Number(dialogueId);
      if (isNaN(dialogueIdNum) || dialogueIdNum <= 0) {
        setError('Invalid dialogue ID');
        setLoading(false);
        setState('error');
        return;
      }
    }

    // Reset all state when segment or dialogue changes
    setCurrentAudioIndex(0);
    resetCaption();
    const emptyMap = new Map();
    setRecordings(emptyMap);
    setTranscripts(emptyMap);
    setCaptionOnForRecording(emptyMap);
    setCaptionEnabled(true);
    setAutoplayEnabled(true);
    try {
      autoplayBehaviorAcknowledgedRef.current =
        localStorage.getItem(AUTOPLAY_INTRO_NEVER_KEY) === '1';
    } catch {
      autoplayBehaviorAcknowledgedRef.current = false;
    }
    autoplayIntroScheduledRef.current = false;
    deferFirstDialogueAfterBriefingRef.current = false;
    pendingPostSystemAutoplayChimeRef.current = false;
    setShowAutoplayIntroModal(false);
    setAutoplayIntroNeverAgain(false);
    userSessionStartedRef.current = false;
    playingBriefingRef.current = false;
    playDialogueImmediatelyAfterBriefingRef.current = false;
    shouldAutoplayNextSystemClipRef.current = false;
    if (autoplayPreChimeTimerRef.current) {
      clearTimeout(autoplayPreChimeTimerRef.current);
      autoplayPreChimeTimerRef.current = null;
    }
    chimePlaybackGenRef.current += 1;
    pauseActiveChimePlayback();
    destroyChimeAudioElement();
    setMicPrewarmActive(false);
    setMicPrewarmRevokeTick((t) => t + 1);
    setBriefingBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPlayedPartBriefings(new Set());
    briefingTargetPartIdRef.current = null;
    recordingsRef.current = emptyMap; // Reset ref as well
    const emptyAttemptsMap = new Map();
    setAttempts(emptyAttemptsMap);
    attemptsRef.current = emptyAttemptsMap; // Reset ref as well
    setIsRecording(false);
    setShowConfirmDialog(false);
    setUploadProgress(null);
    
    // Cleanup previous audio URL
    if (systemAudioURL) {
      URL.revokeObjectURL(systemAudioURL);
      setSystemAudioURL(null);
    }
    
    // Cancel any ongoing fetch
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
      fetchAbortControllerRef.current = null;
    }

    const accessReady = isMockServiceFlow ? mockAccessReady === true : hasSubscription !== null;
    if (accessReady) {
      if (segmentId) {
        fetchSegment();
      } else if (dialogueId) {
        fetchCategoryDialogue();
      }
    }
  }, [segmentId, dialogueId, hasSubscription, mockAccessReady, isMockServiceFlow]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_staff: profile.is_staff || false
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getSubscriptionStatus();
      const profile = await authAPI.getProfile();
      const isStaff = profile.is_staff || false;
      setHasSubscription(status.has_subscription || isStaff);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasSubscription(false);
    }
  };

  const checkMockAccess = async () => {
    try {
      const profile = await authAPI.getProfile();
      if (profile.is_staff || profile.is_superuser) {
        setMockAccessReady(true);
        setHasSubscription(true);
        return;
      }
      const summary = await mockCreditsAPI.getSummary();
      if (!summary.can_take_mock) {
        setError('You need at least 1 mock credit to start this test. Go to Mock Service to request credits.');
        setState('error');
        setMockAccessReady(false);
        return;
      }
      setMockAccessReady(true);
    } catch (error) {
      console.error('Error checking mock credits:', error);
      setError('Could not verify mock credits. Try again from Mock Service.');
      setState('error');
      setMockAccessReady(false);
    }
  };

  const fetchSegment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate segmentId again
      const segmentIdNum = Number(segmentId);
      if (isNaN(segmentIdNum) || segmentIdNum <= 0) {
        throw new Error('Invalid segment ID');
      }
      
      const segmentData = await publicStructuralSegmentsAPI.getSegment(segmentIdNum);
      setSegment(segmentData);
      
      if (segmentData.audios.length === 0) {
        setError('This segment has no audio files');
        setState('error');
      } else {
        setState('ready');
      }
    } catch (err: any) {
      console.error('Error fetching segment:', err);
      setError(err.message || 'Failed to load segment');
      setState('error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryDialogue = async () => {
    try {
      setLoading(true);
      setError(null);

      const idNum = Number(dialogueId);
      if (isNaN(idNum) || idNum <= 0) {
        throw new Error('Invalid dialogue ID');
      }

      const data = await dialogueCategoriesAPI.getDialogue(idNum);

      if (isMockServiceFlow && data.user_has_mock_submission) {
        const profile = await authAPI.getProfile();
        if (!profile.is_staff && !profile.is_superuser) {
          setCategoryDialogue(null);
          setError(
            'You have already completed a mock test for this dialogue. Each dialogue can only be taken once.'
          );
          setState('error');
          return;
        }
      }

      setCategoryDialogue(data);

      if ((data.audios ?? []).length === 0) {
        setError('This dialogue has no audio files');
        setState('error');
      } else {
        setState('ready');
      }
    } catch (err: any) {
      console.error('Error fetching dialogue:', err);
      setError(err.message || 'Failed to load dialogue');
      setState('error');
    } finally {
      setLoading(false);
    }
  };

  // Load audio when state is ready and audio index changes
  useEffect(() => {
    // Cleanup function for previous audio URL
    const cleanup = () => {
      if (systemAudioURL) {
        console.log('[AssessmentPage] Cleanup: revoking previous blob URL');
        URL.revokeObjectURL(systemAudioURL);
        setSystemAudioURL(null);
      }
    };

    // Cancel any ongoing fetch
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
      fetchAbortControllerRef.current = null;
    }

    // Get current audio safely - use sortedAudios from the component scope
    // Note: sortedAudios is computed from segment?.audios, so it should be available
    const currentAudio = sortedAudios[currentAudioIndex];
    const currentAudioId = currentAudio?.audio_file_data?.id;

    console.log('[AssessmentPage] useEffect for audio loading triggered:', {
      state,
      currentAudioIndex,
      totalAudios,
      hasSystemAudioURL: !!systemAudioURL,
      currentAudioId,
      sortedAudiosLength: sortedAudios.length,
      hasCurrentAudio: !!currentAudio
    });

    if (state === 'ready' && currentAudioIndex < totalAudios && currentAudioId) {
      // First, check if we have a recording for the current audio
      // If yes, skip to next audio automatically
      const hasRecording = recordingsRef.current.has(currentAudioIndex);
      if (hasRecording && !skipAutoAdvanceRef.current) {
        const nextIndex = currentAudioIndex + 1;
        if (nextIndex < totalAudios) {
          console.log('[AssessmentPage] useEffect: Recording exists for current audio, auto-advancing to next:', {
            currentIndex: currentAudioIndex,
            nextIndex: nextIndex
          });
          
          // Cleanup previous audio URL
          if (systemAudioURL) {
            URL.revokeObjectURL(systemAudioURL);
            setSystemAudioURL(null);
            currentAudioIdRef.current = null;
          }
          
          // Move to next audio
          setCurrentAudioIndex(nextIndex);
          return; // Exit early, let useEffect run again with new index
        } else {
          console.log('[AssessmentPage] useEffect: All recordings complete');
          setState('completed');
          return;
        }
      }

      const skipDialogueFetch = Boolean(
        dialogueId &&
          dialogueMatchesRoute &&
          categoryDialogue &&
          needsBriefingBeforeClip(
            categoryDialogue,
            sortedDialogueAudiosFlat,
            currentAudioIndex,
            playedPartBriefings
          ) &&
          !skipAutoAdvanceRef.current
      );
      if (skipDialogueFetch) {
        if (systemAudioURL && currentAudioIdRef.current === currentAudioId) {
          URL.revokeObjectURL(systemAudioURL);
          setSystemAudioURL(null);
          currentAudioIdRef.current = null;
        }
        return;
      }
      
      // Check if we need to fetch new audio (different ID or no URL)
      const needsNewFetch = !systemAudioURL || currentAudioIdRef.current !== currentAudioId;
      
      if (needsNewFetch) {
        // Cleanup previous URL if exists
        cleanup();
        
        // Update current audio ID ref
        currentAudioIdRef.current = currentAudioId;
        
        // Create new AbortController for this fetch
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        
        console.log('[AssessmentPage] Fetching audio blob for audio ID:', currentAudioId);
        
          // Fetch audio as blob with token
        recordingsAPI.fetchAudioBlob(currentAudioId)
            .then(blobURL => {
            // Check if fetch was aborted
            if (abortController.signal.aborted) {
              console.log('[AssessmentPage] Fetch was aborted, cleaning up blob URL');
              URL.revokeObjectURL(blobURL);
              return;
            }
            
              console.log('[AssessmentPage] Audio blob fetched successfully, URL:', blobURL.substring(0, 50) + '...');
              setSystemAudioURL(blobURL);
            fetchAbortControllerRef.current = null;
            if (playDialogueImmediatelyAfterBriefingRef.current) {
              playDialogueImmediatelyAfterBriefingRef.current = false;
              setTimeout(() => setState('playing'), 50);
            } else if (shouldAutoplayNextSystemClipRef.current) {
              shouldAutoplayNextSystemClipRef.current = false;
              setTimeout(() => setState('playing'), 80);
            } else if (pendingPlayAfterIndexChangeRef.current) {
              pendingPlayAfterIndexChangeRef.current = false;
              userSessionStartedRef.current = true;
              setTimeout(() => setState('playing'), 80);
            }
            })
            .catch(err => {
            // Ignore abort errors
            if (err.name === 'AbortError' || abortController.signal.aborted) {
              console.log('[AssessmentPage] Fetch was aborted');
              return;
            }
            
              console.error('[AssessmentPage] Error fetching audio:', err);
            // Show user-friendly error message
            const errorMessage = err.message || 'Failed to load audio file';
            setError(errorMessage);
              setState('error');
            fetchAbortControllerRef.current = null;
            });
      } else {
        console.log('[AssessmentPage] SystemAudioURL already exists for current audio, skipping fetch');
      }
    } else if (state === 'completed') {
      // Clear audio URL when completed
      cleanup();
      currentAudioIdRef.current = null;
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      // Only cleanup if this is unmount (not dependency change)
      // For dependency changes, cleanup is handled above
    };
  }, [
    state,
    currentAudioIndex,
    totalAudios,
    systemAudioURL,
    dialogueId,
    dialogueMatchesRoute,
    categoryDialogue,
    sortedDialogueAudiosFlat,
    playedPartBriefings,
  ]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing fetch
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
        fetchAbortControllerRef.current = null;
      }
      
      // Cleanup blob URL
      if (systemAudioURL) {
        console.log('[AssessmentPage] Cleanup on unmount: revoking blob URL');
        URL.revokeObjectURL(systemAudioURL);
      }
    };
  }, [systemAudioURL]); // Track systemAudioURL for cleanup

  const handleStart = () => {
    // Use ref to get latest recordings value (avoids closure issues)
    const currentRecordings = recordingsRef.current;
    
    console.log('[AssessmentPage] handleStart called:', {
      state,
      currentAudioIndex,
      totalAudios,
      hasSystemAudioURL: !!systemAudioURL,
      currentAudioId: sortedAudios[currentAudioIndex]?.audio_file_data?.id,
      hasRecordingForCurrent: currentRecordings.has(currentAudioIndex),
      recordingsSize: currentRecordings.size,
      recordingsKeys: Array.from(currentRecordings.keys())
    });

    // First, check if we have a recording for the current audio
    // If yes, move to next audio BEFORE playing
    const hasRecording = currentRecordings.has(currentAudioIndex);
    console.log('[AssessmentPage] Checking for recording:', {
      currentIndex: currentAudioIndex,
      hasRecording: hasRecording,
      recordingsSize: currentRecordings.size,
      recordingsKeys: Array.from(currentRecordings.keys())
    });
    
    if (hasRecording && !skipAutoAdvanceRef.current) {
      const nextIndex = currentAudioIndex + 1;
      if (nextIndex < totalAudios) {
        console.log('[AssessmentPage] Recording exists for current audio, moving to next audio:', {
          currentIndex: currentAudioIndex,
          nextIndex: nextIndex,
          hasRecording: hasRecording,
          totalRecordings: currentRecordings.size
        });
        
        // Cleanup previous audio URL first
        if (systemAudioURL) {
          URL.revokeObjectURL(systemAudioURL);
          setSystemAudioURL(null);
          currentAudioIdRef.current = null;
        }
        
        // Update audio index - this will trigger useEffect to load new audio
        resetCaption();
        setCurrentAudioIndex(nextIndex);
        
        // State will be set to 'ready' by useEffect, then we need to play
        // But we can't play here because audio hasn't loaded yet
        // So we'll let the useEffect handle loading, and then manually trigger play
        // Actually, we should just return here and let user click Start again for the new audio
        return;
      } else {
        console.log('[AssessmentPage] All recordings complete');
        setState('completed');
        return;
      }
    }

    const startBriefingFirst =
      state === 'ready' &&
      isDialogueCategoryFlow &&
      categoryDialogue &&
      needsBriefingBeforeClip(
        categoryDialogue,
        sortedDialogueAudiosFlat,
        currentAudioIndex,
        playedPartBriefings
      ) &&
      briefingBlobUrl;

    if (startBriefingFirst) {
      const partId = sortedDialogueAudiosFlat[currentAudioIndex]?.part;
      briefingTargetPartIdRef.current = partId ?? null;
      userSessionStartedRef.current = true;
      playingBriefingRef.current = true;
      setState('playing');
      return;
    }

    // If no recording exists, increment attempts for current audio when Start is clicked
    // This should happen regardless of state
    setAttempts(prev => {
      const newMap = new Map(prev);
      const currentAttempts = newMap.get(currentAudioIndex) || 0;
      newMap.set(currentAudioIndex, currentAttempts + 1);
      attemptsRef.current = newMap;
      console.log('[AssessmentPage] Attempts incremented for audio (Start):', {
        audioIndex: currentAudioIndex,
        attempts: currentAttempts + 1,
        previousAttempts: currentAttempts
      });
      // Force re-render by updating trigger
      setAttemptsUpdateTrigger(prev => prev + 1);
      return newMap;
    });
    
    // Now proceed with playing audio if conditions are met
    if (state === 'ready' && currentAudioIndex < totalAudios) {
      
      // Ensure audio URL is loaded before playing
      const audio = sortedAudios[currentAudioIndex];
      if (audio?.audio_file_data?.id) {
        const expectedAudioId = audio.audio_file_data.id;
        
        // Check if we have the correct URL for current audio
        const hasCorrectURL = systemAudioURL && currentAudioIdRef.current === expectedAudioId;
        
        if (!hasCorrectURL) {
          console.log('[AssessmentPage] SystemAudioURL is missing or incorrect, fetching audio blob first');
          
          // Cancel any ongoing fetch
          if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
          }
          
          // Cleanup old URL if exists
          if (systemAudioURL) {
            URL.revokeObjectURL(systemAudioURL);
            setSystemAudioURL(null);
          }
          
          // Create new AbortController
          const abortController = new AbortController();
          fetchAbortControllerRef.current = abortController;
          currentAudioIdRef.current = expectedAudioId;
          
          // Load audio first
          recordingsAPI.fetchAudioBlob(expectedAudioId)
            .then(blobURL => {
              // Check if fetch was aborted
              if (abortController.signal.aborted) {
                URL.revokeObjectURL(blobURL);
                return;
              }
              
              console.log('[AssessmentPage] Audio blob fetched in handleStart, setting state to playing');
              setSystemAudioURL(blobURL);
              fetchAbortControllerRef.current = null;
              // Use setTimeout to ensure state update happens after URL is set
              setTimeout(() => {
                console.log('[AssessmentPage] Setting state to playing after URL is set');
                userSessionStartedRef.current = true;
                setState('playing');
              }, 50);
            })
            .catch(err => {
              // Ignore abort errors
              if (err.name === 'AbortError' || abortController.signal.aborted) {
                return;
              }
              
              console.error('[AssessmentPage] Error fetching audio in handleStart:', err);
              // Show user-friendly error message
              const errorMessage = err.message || 'Failed to load audio file';
              setError(errorMessage);
              setState('error');
              fetchAbortControllerRef.current = null;
            });
        } else {
          console.log('[AssessmentPage] SystemAudioURL exists and is correct, setting state to playing directly');
          userSessionStartedRef.current = true;
          setState('playing');
        }
      } else {
        console.warn('[AssessmentPage] No audio ID found for current audio');
      }
    } else {
      console.warn('[AssessmentPage] handleStart called but conditions not met:', {
        state,
        currentAudioIndex,
        totalAudios
      });
    }
  };

  const runAutoplayChimeSequence = useCallback(() => {
    const generation = chimePlaybackGenRef.current + 1;
    chimePlaybackGenRef.current = generation;
    const isStale = () => chimePlaybackGenRef.current !== generation;

    if (autoplayPreChimeTimerRef.current) {
      clearTimeout(autoplayPreChimeTimerRef.current);
      autoplayPreChimeTimerRef.current = null;
    }
    pauseActiveChimePlayback();

    const startAutoplayRecording = () => {
      // Chime always plays (see handleSystemAudioComplete); auto-starting the
      // recording afterwards is still an autoplay-only behavior — manual mode
      // just plays the chime and waits for the user to press Start Recording.
      if (!effectiveAutoplayEnabledRef.current) return;
      if (!canAutoStartRecordingRef.current) return;
      queueMicrotask(() => {
        setState('recording');
        setIsRecording(true);
      });
    };

    const url = chimeBlobUrlRef.current;
    if (!url) {
      startAutoplayRecording();
      return;
    }

    let rawChimeEl = chimeAudioElementRef.current;
    if (!rawChimeEl) {
      rawChimeEl = new Audio();
      rawChimeEl.preload = 'auto';
      chimeAudioElementRef.current = rawChimeEl;
    }
    const audioEl: HTMLAudioElement = rawChimeEl;

    if (chimeBlobSrcRef.current !== url) {
      audioEl.src = url;
      chimeBlobSrcRef.current = url;
      audioEl.load();
    }

    autoplayChimeAudioRef.current = audioEl;
    audioEl.volume = 1.0;
    audioEl.currentTime = 0;

    const clearChimeRecordTimer = () => {
      if (autoplayPreChimeTimerRef.current) {
        clearTimeout(autoplayPreChimeTimerRef.current);
        autoplayPreChimeTimerRef.current = null;
      }
    };

    const scheduleChimeSafetyTimeout = () => {
      clearChimeRecordTimer();
      autoplayPreChimeTimerRef.current = setTimeout(() => {
        autoplayPreChimeTimerRef.current = null;
        if (isStale() || autoplayChimeAudioRef.current !== audioEl) return;
        pauseActiveChimePlayback();
        startAutoplayRecording();
      }, CHIME_PLAYBACK_SAFETY_TIMEOUT_MS);
    };

    const releaseChimePlayback = () => {
      if (autoplayChimeAudioRef.current === audioEl) {
        pauseActiveChimePlayback();
      }
    };

    const beginRecordingAfterChimePause = () => {
      if (isStale() || autoplayChimeAudioRef.current !== audioEl) return;
      clearChimeRecordTimer();
      const go = () => {
        if (isStale()) return;
        autoplayPreChimeTimerRef.current = null;
        releaseChimePlayback();
        startAutoplayRecording();
      };
      if (RECORDING_PAUSE_AFTER_CHIME_END_MS <= 0) {
        go();
      } else {
        autoplayPreChimeTimerRef.current = setTimeout(go, RECORDING_PAUSE_AFTER_CHIME_END_MS);
      }
    };

    const playChimeWithRetry = async () => {
      for (let attempt = 0; attempt < CHIME_PLAY_MAX_ATTEMPTS; attempt += 1) {
        if (isStale() || autoplayChimeAudioRef.current !== audioEl) return;
        try {
          audioEl.currentTime = 0;
          await audioEl.play();
          if (isStale() || autoplayChimeAudioRef.current !== audioEl) return;
          // Prewarm mic only after chime actually starts — avoids getUserMedia racing play().
          setMicPrewarmActive(true);
          scheduleChimeSafetyTimeout();
          return;
        } catch {
          if (attempt < CHIME_PLAY_MAX_ATTEMPTS - 1) {
            await new Promise((resolve) =>
              setTimeout(resolve, CHIME_PLAY_RETRY_BASE_MS + attempt * 50)
            );
          }
        }
      }
      if (!isStale() && autoplayChimeAudioRef.current === audioEl) {
        clearChimeRecordTimer();
        pauseActiveChimePlayback();
        startAutoplayRecording();
      }
    };

    audioEl.onended = null;
    audioEl.onerror = null;
    audioEl.addEventListener('ended', beginRecordingAfterChimePause, { once: true });
    audioEl.onerror = () => {
      if (isStale()) return;
      clearChimeRecordTimer();
      void playChimeWithRetry();
    };

    void playChimeWithRetry();
  }, [pauseActiveChimePlayback]);

  const handleAutoplayIntroConfirm = () => {
    if (autoplayIntroNeverAgain) {
      try {
        localStorage.setItem(AUTOPLAY_INTRO_NEVER_KEY, '1');
      } catch {
        /* ignore */
      }
    }
    autoplayBehaviorAcknowledgedRef.current = true;
    setAutoplayIntroNeverAgain(false);
    setShowAutoplayIntroModal(false);

    const hadDeferredBriefing = deferFirstDialogueAfterBriefingRef.current;
    deferFirstDialogueAfterBriefingRef.current = false;

    const hadPendingChime = pendingPostSystemAutoplayChimeRef.current;
    pendingPostSystemAutoplayChimeRef.current = false;

    // After briefing, the first examiner clip must play first; chime runs only after that clip
    // ends (same as other clips), via handleSystemAudioComplete → runAutoplayChimeSequence.
    if (hadDeferredBriefing) {
      playDialogueImmediatelyAfterBriefingRef.current = true;
      const expectedId = sortedAudios[currentAudioIndex]?.audio_file_data?.id;
      const hasDialogueBlobReady =
        expectedId != null &&
        !!systemAudioURL &&
        currentAudioIdRef.current === expectedId;
      if (hasDialogueBlobReady) {
        requestAnimationFrame(() => {
          setState((s) => (s === 'ready' ? 'playing' : s));
        });
      }
    } else if (hadPendingChime) {
      queueMicrotask(() => runAutoplayChimeSequence());
    }
  };

  const handleSystemAudioComplete = () => {
    if (playingBriefingRef.current) {
      playingBriefingRef.current = false;
      const tid = briefingTargetPartIdRef.current;
      briefingTargetPartIdRef.current = null;
      if (tid != null) {
        setPlayedPartBriefings((prev) => new Set(prev).add(tid));
      }
      const needIntro =
        effectiveAutoplayEnabled && !autoplayBehaviorAcknowledgedRef.current;
      if (needIntro) {
        deferFirstDialogueAfterBriefingRef.current = true;
        setShowAutoplayIntroModal(true);
      } else {
        playDialogueImmediatelyAfterBriefingRef.current = true;
      }
      setState('ready');
      return;
    }

    setState('ready');

    // Chime plays after every examiner clip regardless of the autoplay toggle;
    // only auto-starting the recording afterwards is gated on autoplay (see
    // startAutoplayRecording in runAutoplayChimeSequence).
    if ((isDialogueCategoryFlow || isStructuralSegmentFlow) && userSessionStartedRef.current) {
      if (effectiveAutoplayEnabled && !autoplayBehaviorAcknowledgedRef.current) {
        pendingPostSystemAutoplayChimeRef.current = true;
        return;
      }

      if (autoplayPreChimeTimerRef.current) {
        clearTimeout(autoplayPreChimeTimerRef.current);
        autoplayPreChimeTimerRef.current = null;
      }
      pauseActiveChimePlayback();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => runAutoplayChimeSequence());
      });
    }
  };

  const handleStartRecording = () => {
    cancelAutoplayRecordingSchedule();
    // User explicitly starts recording after system audio
    console.log('[AssessmentPage] handleStartRecording called:', {
      state,
      currentAudioIndex,
      hasSystemAudioURL: !!systemAudioURL
    });
    
    if (state === 'ready' && systemAudioURL) {
    setState('recording');
    setIsRecording(true);
    }
  };

  const goToPreviousClip = () => {
    if (currentAudioIndex <= 0) return;

    cancelAutoplayRecordingSchedule();
    shouldAutoplayNextSystemClipRef.current = false;
    skipAutoAdvanceRef.current = true;
    pendingPlayAfterIndexChangeRef.current = true;
    playingBriefingRef.current = false;
    briefingTargetPartIdRef.current = null;
    resetCaption();

    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
      fetchAbortControllerRef.current = null;
    }
    if (systemAudioURL) {
      URL.revokeObjectURL(systemAudioURL);
      setSystemAudioURL(null);
    }
    currentAudioIdRef.current = null;
    setIsRecording(false);
    setCurrentAudioIndex((idx) => Math.max(0, idx - 1));
    setState('ready');
  };

  const handlePreviousClip = () => {
    if (currentAudioIndex <= 0 || effectiveAutoplayEnabled || isMockServiceFlow) return;

    cancelAutoplayRecordingSchedule();
    shouldAutoplayNextSystemClipRef.current = false;

    if (isRecording) {
      pendingPreviousAfterStopRef.current = true;
      stopCaption();
      setIsRecording(false);
      return;
    }

    goToPreviousClip();
  };

  const handleRecordingComplete = (blob: Blob, duration?: number) => {
    const hadCaptionOn = !isMockServiceFlow && captionEnabled;
    const capTranscript = transcriptRef.current?.trim() || '';

    setCaptionOnForRecording(prev => new Map(prev).set(currentAudioIndex, hadCaptionOn));

    setTranscripts(prev => {
      const next = new Map(prev);
      if (hadCaptionOn && capTranscript) {
        next.set(currentAudioIndex, capTranscript);
      } else {
        next.delete(currentAudioIndex);
      }
      return next;
    });
    // Save recording
    setRecordings(prev => {
      const newMap = new Map(prev);
      newMap.set(currentAudioIndex, blob);
      // Update ref as well to keep it in sync
      recordingsRef.current = newMap;
      console.log('[AssessmentPage] handleRecordingComplete: Recording saved', {
        audioIndex: currentAudioIndex,
        blobSize: blob.size,
        duration: duration,
        totalRecordings: newMap.size,
        recordingsKeys: Array.from(newMap.keys())
      });
      return newMap;
    });
    
    // Save duration if provided
    if (duration !== undefined && duration > 0) {
      setRecordingDurations(prev => {
        const newMap = new Map(prev);
        newMap.set(currentAudioIndex, duration);
        return newMap;
      });
    }
    
    // Reset attempts for current audio after recording is complete
    setAttempts(prev => {
      const newMap = new Map(prev);
      newMap.set(currentAudioIndex, 0);
      attemptsRef.current = newMap;
      console.log('[AssessmentPage] Attempts reset for audio after recording complete:', {
        audioIndex: currentAudioIndex
      });
      // Force re-render by updating trigger
      setAttemptsUpdateTrigger(prev => prev + 1);
      return newMap;
    });
    
    setIsRecording(false);

    const isLastClip = currentAudioIndex >= totalAudios - 1;
    if (pendingPreviousAfterStopRef.current) {
      pendingPreviousAfterStopRef.current = false;
      goToPreviousClip();
      return;
    }

    skipAutoAdvanceRef.current = false;

    if (effectiveAutoplayEnabled && (isDialogueCategoryFlow || isStructuralSegmentFlow) && !isLastClip) {
      shouldAutoplayNextSystemClipRef.current = true;
    }

    // After recording is complete, check if we should move to next audio or stay ready
    // If we have a recording for current audio, we'll move to next when user clicks Start
    // For now, just go to ready state
    setState('ready');
  };

  const handleFinishAttempt = () => {
    // This is called when user clicks "Finish Attempt" button
    // The recording will be stopped and onRecordingComplete will be called
    if (isRecording) {
      stopCaption(); // Stop caption immediately for better UX
      setIsRecording(false);
    }
  };

  const recordingStreamRef = useRef<MediaStream | null>(null);

  const handleRecordingStreamReady = useCallback((stream: MediaStream) => {
    recordingStreamRef.current = stream;
    if (!isMockServiceFlow && captionEnabled) {
      startCaption(stream);
    }
  }, [startCaption, isMockServiceFlow, captionEnabled]);

  const handleRecordingStopped = useCallback(() => {
    recordingStreamRef.current = null;
    stopCaption();
  }, [stopCaption]);

  const handleCaptionRetry = useCallback(() => {
    resetCaption();
    if (recordingStreamRef.current?.active && !isMockServiceFlow && captionEnabled) {
      startCaption(recordingStreamRef.current);
    }
  }, [resetCaption, startCaption, isMockServiceFlow, captionEnabled]);

  // When user toggles caption while recording: off -> stop, on -> start if stream available
  useEffect(() => {
    if (!isMockServiceFlow && isRecording) {
      if (!captionEnabled) {
        stopCaption();
      } else if (recordingStreamRef.current?.active) {
        startCaption(recordingStreamRef.current);
      }
    }
  }, [captionEnabled, isRecording, stopCaption, startCaption, isMockServiceFlow]);

  const handlePlaybackComplete = () => {
    // After user recording playback finishes, just go back to ready state
    // User can manually start next audio if they want
    setState('ready');
  };

  const handleRepeat = () => {
    const partId = sortedDialogueAudiosFlat[currentAudioIndex]?.part;
    const bid =
      partId != null && categoryDialogue ? getBriefingFileIdForPart(categoryDialogue, partId) : undefined;
    const canRepeatBriefing =
      isDialogueCategoryFlow &&
      categoryDialogue &&
      partId != null &&
      bid != null &&
      isFirstClipOfPart(sortedDialogueAudiosFlat, currentAudioIndex);
    const repeatBriefingOnly =
      canRepeatBriefing &&
      !!briefingBlobUrl &&
      (!playedPartBriefings.has(partId!) || playingBriefingRef.current);

    if (repeatBriefingOnly) {
      playingBriefingRef.current = true;
      briefingTargetPartIdRef.current = partId ?? null;
    } else {
      setAttempts(prev => {
        const newMap = new Map(prev);
        const currentAttempts = newMap.get(currentAudioIndex) || 0;
        newMap.set(currentAudioIndex, currentAttempts + 1);
        attemptsRef.current = newMap;
        console.log('[AssessmentPage] Attempts incremented for audio (Repeat):', {
          audioIndex: currentAudioIndex,
          attempts: currentAttempts + 1
        });
        setAttemptsUpdateTrigger((prev) => prev + 1);
        return newMap;
      });
    }

    if (repeatBriefingOnly && bid != null) {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      if (systemAudioURL) {
        setTimeout(() => URL.revokeObjectURL(systemAudioURL), 100);
        setSystemAudioURL(null);
      }
      currentAudioIdRef.current = null;
      const abortController = new AbortController();
      fetchAbortControllerRef.current = abortController;
      recordingsAPI
        .fetchAudioBlob(bid)
        .then((blobURL) => {
          if (abortController.signal.aborted) {
            URL.revokeObjectURL(blobURL);
            return;
          }
          setBriefingBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return blobURL;
          });
          fetchAbortControllerRef.current = null;
          setState('playing');
        })
        .catch((err) => {
          if (err.name === 'AbortError' || abortController.signal.aborted) return;
          console.error('Error re-fetching briefing:', err);
          setError(err.message || 'Failed to load briefing');
          setState('error');
          fetchAbortControllerRef.current = null;
        });
      return;
    }

    // Restart current audio
    if (currentAudio && currentAudio.audio_file_data?.id) {
      const audioId = currentAudio.audio_file_data.id;
      
      // Cancel any ongoing fetch
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      
      // Revoke old URL if exists (but wait a bit to ensure audio element is done using it)
      if (systemAudioURL) {
        // Use setTimeout to ensure audio element has released the URL
        setTimeout(() => {
        URL.revokeObjectURL(systemAudioURL);
        }, 100);
      }
      
      // Create new AbortController
      const abortController = new AbortController();
      fetchAbortControllerRef.current = abortController;
      currentAudioIdRef.current = audioId;
      
      // Fetch new blob URL
      recordingsAPI.fetchAudioBlob(audioId)
        .then(blobURL => {
          // Check if fetch was aborted
          if (abortController.signal.aborted) {
            URL.revokeObjectURL(blobURL);
            return;
          }
          
          setSystemAudioURL(blobURL);
          fetchAbortControllerRef.current = null;
          setState('playing');
        })
        .catch(err => {
          // Ignore abort errors
          if (err.name === 'AbortError' || abortController.signal.aborted) {
            return;
          }
          
          console.error('Error fetching audio:', err);
          // Show user-friendly error message
          const errorMessage = err.message || 'Failed to load audio file';
          setError(errorMessage);
          setState('error');
          fetchAbortControllerRef.current = null;
        });
    }
  };

  const handleSubmit = async () => {
    // Use ref to get latest value
    if (recordingsRef.current.size !== totalAudios) {
      alert('Please complete all recordings before submitting');
      return;
    }

    // Reset review states when opening dialog
    setPlayingReviewRecordingIndex(null);
    setReviewAudioURL(null);
    if (reviewAudioRef.current) {
      reviewAudioRef.current.pause();
      reviewAudioRef.current.src = '';
    }
    
    // Automatically mark all recordings as reviewed since they exist
    const allRecordingsReviewed = new Set<number>();
    recordings.forEach((_, audioIndex) => {
      allRecordingsReviewed.add(audioIndex);
    });
    setReviewedRecordings(allRecordingsReviewed);
    setReviewRowExpand(new Map());

    setShowConfirmDialog(true);
  };
  
  const handleCancel = () => {
    // Close dialog
    setShowConfirmDialog(false);
    
    // Clean up review states
    setPlayingReviewRecordingIndex(null);
    setReviewedRecordings(new Set());
    setReviewAudioURL(null);
    if (reviewAudioRef.current) {
      reviewAudioRef.current.pause();
      reviewAudioRef.current.src = '';
    }
    
    // Reset all recordings and state - start from beginning
    setRecordings(new Map());
    recordingsRef.current.clear();
    setTranscripts(new Map());
    setCaptionOnForRecording(new Map());
    setCaptionEnabled(true);
    setAutoplayEnabled(true);
    try {
      autoplayBehaviorAcknowledgedRef.current =
        localStorage.getItem(AUTOPLAY_INTRO_NEVER_KEY) === '1';
    } catch {
      autoplayBehaviorAcknowledgedRef.current = false;
    }
    autoplayIntroScheduledRef.current = false;
    deferFirstDialogueAfterBriefingRef.current = false;
    pendingPostSystemAutoplayChimeRef.current = false;
    setShowAutoplayIntroModal(false);
    setAutoplayIntroNeverAgain(false);
    if (autoplayPreChimeTimerRef.current) {
      clearTimeout(autoplayPreChimeTimerRef.current);
      autoplayPreChimeTimerRef.current = null;
    }
    chimePlaybackGenRef.current += 1;
    pauseActiveChimePlayback();
    destroyChimeAudioElement();
    setMicPrewarmActive(false);
    setMicPrewarmRevokeTick((t) => t + 1);
    userSessionStartedRef.current = false;
    playingBriefingRef.current = false;
    playDialogueImmediatelyAfterBriefingRef.current = false;
    shouldAutoplayNextSystemClipRef.current = false;
    if (systemAudioURL) {
      URL.revokeObjectURL(systemAudioURL);
    }
    setSystemAudioURL(null);
    currentAudioIdRef.current = null;
    setBriefingBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setPlayedPartBriefings(new Set());
    briefingTargetPartIdRef.current = null;
    setRecordingDurations(new Map());
    setAttempts(new Map());
    attemptsRef.current.clear();
    setCurrentAudioIndex(0);
    setState('ready');
    setUploadProgress(null);
  };
  
  const handlePlayReviewRecording = (audioIndex: number) => {
    const blob = recordings.get(audioIndex);
    if (!blob) return;
    
    // Stop current playback if any
    if (reviewAudioRef.current) {
      reviewAudioRef.current.pause();
      reviewAudioRef.current.src = '';
    }
    
    // Clean up previous URL
    if (reviewAudioURL) {
      URL.revokeObjectURL(reviewAudioURL);
    }
    
    // Create new URL for this recording
    const url = URL.createObjectURL(blob);
    setReviewAudioURL(url);
    setPlayingReviewRecordingIndex(audioIndex);
    
    // Play audio
    setTimeout(() => {
      const audio = reviewAudioRef.current;
      if (audio) {
        audio.src = url;
        audio.play();
      }
    }, 100);
  };
  
  const handleStopReviewRecording = () => {
    if (reviewAudioRef.current) {
      reviewAudioRef.current.pause();
      reviewAudioRef.current.currentTime = 0;
    }
    setPlayingReviewRecordingIndex(null);
  };
  
  const handleMarkRecordingReviewed = (audioIndex: number) => {
    setReviewedRecordings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(audioIndex)) {
        newSet.delete(audioIndex);
      } else {
        newSet.add(audioIndex);
      }
      return newSet;
    });
  };

  const patchReviewRowExpand = useCallback((audioIndex: number, patch: Partial<ReviewRowExpandState>) => {
    setReviewRowExpand((prev) => {
      const next = new Map(prev);
      const cur: ReviewRowExpandState = next.get(audioIndex) ?? { ...DEFAULT_REVIEW_EXPAND };
      next.set(audioIndex, { ...cur, ...patch });
      return next;
    });
  }, []);
  
  useEffect(() => {
    if (!showConfirmDialog) return;
    document.querySelectorAll<HTMLAudioElement>('.review-system-audio').forEach((el) => {
      applySystemPlaybackRate(el, systemPlaybackRate);
    });
  }, [showConfirmDialog, systemPlaybackRate, reviewRowExpand, recordings]);
  
  // Cleanup audio URL on unmount or when dialog closes
  useEffect(() => {
    return () => {
      if (reviewAudioURL) {
        URL.revokeObjectURL(reviewAudioURL);
      }
    };
  }, [reviewAudioURL]);
  
  // Handle audio ended event
  useEffect(() => {
    const audio = reviewAudioRef.current;
    if (!audio) return;
    
    const handleEnded = () => {
      setPlayingReviewRecordingIndex(null);
    };
    
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [reviewAudioURL]);

  const confirmSubmit = async () => {
    setShowConfirmDialog(false);
    setState('submitting');
    const progressTotal = isMockTestSubmission ? totalAudios : 1;
    setUploadProgress({ current: 0, total: progressTotal });

    try {
      if (!segmentId && !dialogueId) {
        throw new Error('Segment or dialogue ID is required.');
      }
      
      const segmentIdNum = segmentId ? Number(segmentId) : null;
      const dialogueIdNum = dialogueId ? Number(dialogueId) : null;
      
      if (segmentIdNum !== null && (isNaN(segmentIdNum) || segmentIdNum <= 0)) {
        throw new Error('Invalid segment ID');
      }
      if (dialogueIdNum !== null && (isNaN(dialogueIdNum) || dialogueIdNum <= 0)) {
        throw new Error('Invalid dialogue ID');
      }
      
      if (isMockTestSubmission && dialogueIdNum) {
        const mockRecordings = Array.from({ length: totalAudios }, (_, i) => ({
          blob: recordings.get(i)!,
          transcript: transcripts.get(i),
          duration: recordingDurations.get(i),
        }));
        const { submission_id } = await recordingsAPI.submitMockTest(dialogueIdNum, mockRecordings);
        setUploadProgress(null);
        alert('Mock test submitted successfully! You can view your results once they are graded.');
        navigate(`/mock-service/result/${submission_id}`);
        return;
      }
      
      const clips = Array.from({ length: totalAudios }, (_, i) => ({
        audio_index: i,
        attempts: Math.max(1, attemptsRef.current.get(i) || 0),
      }));

      await recordingsAPI.registerPracticeSession({
        segmentId: segmentIdNum,
        dialogueId: dialogueIdNum,
        clips,
      });

      setUploadProgress(null);
      alert(
        'Practice saved. Your recordings were not stored on the server. You can use Practice Again anytime from the list.'
      );
      navigate(
        segmentId
          ? '/material/structured-segments'
          : dialogueCategoriesListPath(
              categoryDialogue?.category ??
                parseCategoryQueryId(searchParams.get('category')) ??
                (location.state as { categoryId?: number } | null)?.categoryId
            )
      );
    } catch (err: any) {
      console.error('Error uploading recordings:', err);
      setError(err.message || 'Failed to upload recordings');
      setState('ready'); // Reset to ready state so user can retry
      setUploadProgress(null);
    }
  };

  const progress = totalAudios > 0 ? ((currentAudioIndex + (state === 'completed' ? 1 : 0)) / totalAudios) * 100 : 0;

  if (loading) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userInfo.email}
            organization={userInfo.organization}
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="loading-container" style={{ padding: '4rem', textAlign: 'center', color: '#ffffff' }}>
              <p>Loading segment...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show completion message if segment is already completed
  if (state === 'completed' && error && error.includes('already completed')) {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userInfo.email}
            organization={userInfo.organization}
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div style={{ 
              padding: '3rem', 
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <div style={{
                fontSize: '4rem',
                color: '#28a745',
                marginBottom: '1rem',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 64 })}
              </div>
              <h2 style={{ 
                color: '#ffffff',
                marginBottom: '1rem',
                fontSize: '1.5rem'
              }}>
                {segmentId ? 'Segment' : 'Dialogue'} Already Completed
              </h2>
              <p style={{ 
                color: '#cccccc',
                marginBottom: '2rem',
                fontSize: '1rem',
                lineHeight: '1.6'
              }}>
                {error}
              </p>
              <button
                onClick={goBackFromAssessmentError}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#3a3a3a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#4a4a4a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#3a3a3a';
                }}
              >
                {isMockServiceFlow && dialogueId
                  ? 'Back to Mock Service'
                  : `Back ${segmentId ? 'to Segments List' : 'to Dialogue Categories'}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && state === 'error') {
    return (
      <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Sidebar />
        <div className="dashboard-main">
          <DashboardHeader
            userEmail={userInfo.email}
            organization={userInfo.organization}
            onLogout={onLogout}
          />
          <div className="dashboard-content">
            <div className="error-container" style={{ padding: '4rem', textAlign: 'center', color: '#E63946' }}>
              {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 48 })}
              <p style={{ marginTop: '1rem', fontSize: '1.2rem' }}>{error}</p>
              <button 
                onClick={goBackFromAssessmentError}
                style={{ 
                  marginTop: '2rem', 
                  padding: '0.75rem 2rem', 
                  background: '#E63946', 
                  color: '#ffffff', 
                  border: 'none', 
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                {isMockServiceFlow && dialogueId ? 'Back to Mock Service' : 'Go Back'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
    <div className="assessment-page">
        <div className="content-header">
          <h1 className="segment-title">
            {isMockServiceFlow && categoryDialogue
              ? getOrCreateMockDialogueLabel(categoryDialogue.id)
              : segment?.name || categoryDialogue?.name || 'Assessment'}
          </h1>
          <div className="content-header-meta">
            <span className="test-type">Persian CCL test</span>
            {segment?.test_category && (
              <span className="test-category">{segment.test_category}</span>
            )}
            {!isMockServiceFlow && categoryDialogue?.category_name && (
              <span className="test-category">{categoryDialogue.category_name}</span>
            )}
          </div>
        </div>

        {inBriefingPlaybackPhase && (
          <p className="assessment-briefing-banner" role="status">
            <strong>Dialogue briefing</strong> — playing now. The first clip in this dialogue starts automatically right
            after this audio ends.
          </p>
        )}
        
        <div className="content-grid">
          <div className="left-column">
            <InstructionsPanel
              onStart={handleStart}
              onFinishAttempt={handleFinishAttempt}
              onRepeat={handleRepeat}
              onPrevious={handlePreviousClip}
              showPrevious={!isMockServiceFlow && !effectiveAutoplayEnabled}
              canGoPrevious={currentAudioIndex > 0}
              isRecording={isRecording}
              disabled={state !== 'ready' && state !== 'playing' && state !== 'playback' && state !== 'recording'}
                />
                
                {/* Progress Info */}
                <div className="assessment-progress-card">
                  <p className="assessment-progress-label">
                    Progress: {currentAudioIndex + (state === 'completed' ? 1 : 0)} / {totalAudios}
                  </p>
                  <div className="assessment-progress-track">
                    <div className="assessment-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* Submit Button - Only show when user finishes the last audio */}
                {allRecordingsComplete && (state === 'ready' || state === 'completed' || state === 'submitting') && (
                  <button
                    type="button"
                    className={`assessment-submit-btn${state === 'submitting' ? ' is-busy' : ''}`}
                    onClick={handleSubmit}
                    disabled={state === 'submitting'}
                  >
                    {state === 'submitting'
                      ? isMockTestSubmission
                        ? `Uploading... (${uploadProgress?.current || 0}/${uploadProgress?.total || 0})`
                        : `Saving practice… (${uploadProgress?.current || 0}/${uploadProgress?.total || 0})`
                      : isMockTestSubmission
                        ? 'Submit All Recordings'
                        : 'Finish practice'}
                  </button>
                )}
          </div>
          
          <div className="right-column">
            <AudioRecorder
                  onRecordingComplete={handleRecordingComplete}
                  systemAudioURL={effectiveSystemAudioURL || undefined}
                  onSystemAudioComplete={handleSystemAudioComplete}
              onFinishAttempt={handleFinishAttempt}
              onPlaybackComplete={handlePlaybackComplete}
              onStartRecording={handleStartRecording}
              onRecordingStreamReady={handleRecordingStreamReady}
              onRecordingStopped={handleRecordingStopped}
              onRepeatSystemAudio={handleRepeat}
              onStart={handleStart}
              onPrevious={handlePreviousClip}
              showPrevious={!isMockServiceFlow && !effectiveAutoplayEnabled}
              canGoPrevious={currentAudioIndex > 0}
                  isRecording={isRecording && state === 'recording'}
                  autoPlay={state === 'playing'}
              autoPlayRecording={false}
                  disabled={state !== 'ready' && state !== 'playing' && state !== 'recording'}
                  attempts={attempts.get(currentAudioIndex) || 0}
                  prewarmMic={micPrewarmActive}
                  micPrewarmRevokeTick={micPrewarmRevokeTick}
                />
            {(isDialogueCategoryFlow || isStructuralSegmentFlow) && !isMockServiceFlow && (
              <div className="assessment-option-block">
                <label className="assessment-option-label">
                  <input
                    type="checkbox"
                    checked={autoplayEnabled}
                    onChange={(e) => setAutoplayEnabled(e.target.checked)}
                    className="assessment-option-checkbox"
                  />
                  Autoplay
                </label>
                <p className="assessment-option-hint">
                  Mirrors exam-style timing: after each examiner clip, a short chime marks your turn; speak, then use{' '}
                  <strong>Next</strong> to stop that take. The first time you use autoplay, a short explanation
                  appears (after briefing when applicable).
                </p>
              </div>
            )}
            {!isMockServiceFlow && (
              <div className="assessment-option-block">
                <label className="assessment-option-label">
                  <input
                    type="checkbox"
                    checked={captionEnabled}
                    onChange={(e) => setCaptionEnabled(e.target.checked)}
                    className="assessment-option-checkbox"
                  />
                  Captioning
                </label>
                <p className="assessment-option-hint">
                  If you turn this off, it stays off for the rest of this session until you turn it on again.
                </p>
              </div>
            )}
            {!isMockServiceFlow && captionEnabled && (
            <RealtimeCaption
              segments={transcript}
              partialText={partialText}
              isActive={state === 'recording'}
              error={captionError}
              onRetry={handleCaptionRetry}
              className="assessment-caption"
            />
            )}
          </div>
        </div>
        
        <div className="bottom-progress-bar">
          <div 
            className="progress-bar-fill" 
                style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="bottom-navigation">
              <button 
                className="nav-btn previous-btn" 
                onClick={() => {
                  if (segmentId) {
                    navigate('/material/structured-segments');
                  } else if (dialogueId) {
                    navigate(
                      dialogueCategoriesListPath(
                        categoryDialogue?.category ??
                          parseCategoryQueryId(searchParams.get('category')) ??
                          (location.state as { categoryId?: number } | null)?.categoryId
                      )
                    );
                  }
                }}
              >
                ← Back {segmentId ? 'to Segments' : 'to Dialogue Categories'}
          </button>
              {allRecordingsComplete && (state === 'ready' || state === 'completed' || state === 'submitting') && (
              <button 
                className="nav-btn next-btn" 
                  disabled={state === 'submitting'}
                onClick={handleSubmit}
              >
                {state === 'submitting'
                  ? isMockTestSubmission
                    ? 'Submitting...'
                    : 'Saving...'
                  : isMockTestSubmission
                    ? 'Submit'
                    : 'Finish'}
          </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAutoplayIntroModal && (
        <div
          className="autoplay-intro-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="autoplay-intro-title"
        >
          <div className="autoplay-intro-modal">
            <h2 id="autoplay-intro-title" className="autoplay-intro-title">
              Autoplay mode
            </h2>
            <div className="autoplay-intro-body">
              <p>
                You are using <strong>Autoplay</strong>, which follows timing similar to a live NAATI-style dialogue exam.
              </p>
              <p>
                After each examiner dialogue clip finishes, a <strong>chime</strong> will play. That sound means it is your
                turn to interpret—start speaking right away. When you have finished that turn, click{' '}
                <strong>Next</strong> to stop recording; the next examiner clip will play automatically when
                applicable.
              </p>
            </div>
            <label className="autoplay-intro-checkbox">
              <input
                type="checkbox"
                checked={autoplayIntroNeverAgain}
                onChange={(e) => setAutoplayIntroNeverAgain(e.target.checked)}
              />
              <span>Don&apos;t show this explanation again</span>
            </label>
            <button type="button" className="autoplay-intro-primary" onClick={handleAutoplayIntroConfirm}>
              I understand
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="review-dialog-overlay">
          <div
            className={`review-dialog-panel${
              isMockTestSubmission ? '' : ' review-dialog-panel--practice'
            }`}
          >
            <div className="review-dialog-content">
              <h3 className="review-dialog-title">
                {isMockTestSubmission ? 'Review & confirm submission' : 'Review & confirm practice'}
              </h3>
              <p className="review-dialog-desc">
                {isMockTestSubmission
                  ? 'Review before submitting. System voice + text vs your recording + expected translation.'
                  : 'Review your takes locally. Confirming registers this practice run (attempt counts only); your audio is not stored on the server.'}
              </p>

              <SystemPlaybackSpeedControl
                value={systemPlaybackRate}
                onChange={setSystemPlaybackRate}
                className="review-system-speed-control"
              />

              <audio ref={reviewAudioRef} />

              <div className="review-dialog-clips">
              {Array.from(recordings.entries())
                .sort(([a], [b]) => a - b)
                .map(([audioIndex, blob]) => {
                  const isPlaying = playingReviewRecordingIndex === audioIndex;
                  const isReviewed = reviewedRecordings.has(audioIndex);
                  const duration = recordingDurations.get(audioIndex);
                  const sysAudio = sortedAudios[audioIndex];
                  const audioData = sysAudio?.audio_file_data as {
                    id?: number;
                    source_text?: string;
                    translations?: string[];
                  } | undefined;
                  const systemStreamUrl =
                    audioData?.id != null ? getAuthenticatedAudioStreamUrl(audioData.id) : null;
                  const sourceText = audioData?.source_text || '';
                  const expectedTranslations = normalizeExpectedTranslations(audioData?.translations);
                  const userTranscript = transcripts.get(audioIndex) || '';
                  const captionWasOnForTake = captionOnForRecording.get(audioIndex);

                  const captionReviewDisplay = (() => {
                    if (isMockServiceFlow) {
                      return {
                        main: 'Live captioning is not used for mock tests.',
                        muted: false
                      };
                    }
                    if (captionWasOnForTake === false) {
                      return {
                        main: 'You turned off captioning for this recording.',
                        muted: true
                      };
                    }
                    if (captionWasOnForTake === true) {
                      if (userTranscript.trim()) {
                        return { main: userTranscript, muted: false };
                      }
                      return {
                        main: '—',
                        muted: true,
                        note: 'Captioning was on, but no text was captured.'
                      };
                    }
                    if (userTranscript.trim()) {
                      return { main: userTranscript, muted: false };
                    }
                    return { main: '—', muted: false };
                  })();

                  const formatTime = (seconds: number) => {
                    if (!seconds || !isFinite(seconds)) return 'N/A';
                    const mins = Math.floor(seconds / 60);
                    const secs = Math.floor(seconds % 60);
                    return `${mins}:${secs.toString().padStart(2, '0')}`;
                  };

                  const expand = reviewRowExpand.get(audioIndex) ?? DEFAULT_REVIEW_EXPAND;
                  const hasSourceText = Boolean(sourceText.trim());
                  const showPrevExpectedControl = expand.expectedRevealed > 1;
                  const showMoreExpectedControl =
                    expand.expectedRevealed > 0 &&
                    expand.expectedRevealed < expectedTranslations.length;

                  return (
                    <div
                      key={audioIndex}
                      className={`review-clip-card${isReviewed ? ' review-clip-card--reviewed' : ''}`}
                    >
                      <div className="review-clip-header">
                        <span className="review-clip-title">Audio {audioIndex + 1}</span>
                        {duration ? <span className="review-clip-duration">{formatTime(duration)}</span> : null}
                        {isReviewed ? (
                          <span className="review-clip-check" aria-label="Reviewed">
                            {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                          </span>
                        ) : null}
                      </div>
                      <div className="review-clip-grid">
                        <div className="review-col review-col--system">
                          <div className="review-col-heading review-col-heading--system">System voice + text</div>
                          {systemStreamUrl ? (
                            <audio
                              controls
                              src={systemStreamUrl}
                              className="review-system-audio"
                              preload="metadata"
                              onLoadedMetadata={(e) =>
                                applySystemPlaybackRate(e.currentTarget, systemPlaybackRate)
                              }
                            />
                          ) : null}
                          {hasSourceText ? (
                            <div className="review-reveal-group">
                              {isMockTestSubmission ? (
                                <>
                                  <button
                                    type="button"
                                    className="review-reveal-toggle"
                                    aria-expanded={expand.showSourceText}
                                    onClick={() =>
                                      patchReviewRowExpand(audioIndex, {
                                        showSourceText: !expand.showSourceText,
                                      })
                                    }
                                  >
                                    {expand.showSourceText ? 'Hide text' : 'Show text'}
                                  </button>
                                  {expand.showSourceText ? (
                                    <div className="review-collapsible-body">{sourceText}</div>
                                  ) : null}
                                </>
                              ) : (
                                <div className="review-instruction-block">
                                  <div className="review-instruction-header">
                                    <span className="review-instruction-label">
                                      Grammatical Instruction to Create Translations
                                    </span>
                                    <button
                                      type="button"
                                      className="review-reveal-toggle review-reveal-toggle--compact"
                                      aria-expanded={expand.showSourceText}
                                      aria-controls={`review-instruction-${audioIndex}`}
                                      onClick={() =>
                                        patchReviewRowExpand(audioIndex, {
                                          showSourceText: !expand.showSourceText,
                                        })
                                      }
                                    >
                                      {expand.showSourceText ? 'Hide' : 'Show'}
                                    </button>
                                  </div>
                                  {expand.showSourceText ? (
                                    <div
                                      id={`review-instruction-${audioIndex}`}
                                      className="review-collapsible-body"
                                    >
                                      {sourceText}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="review-empty-hint">No reference text for this clip.</p>
                          )}
                        </div>
                        <div className="review-col review-col--user">
                          <div className="review-col-heading review-col-heading--user">Your recording</div>
                          <div className="review-user-playback">
                            <button
                              type="button"
                              className="review-play-btn"
                              onClick={() => {
                                if (isPlaying) handleStopReviewRecording();
                                else handlePlayReviewRecording(audioIndex);
                              }}
                              aria-label={isPlaying ? 'Pause your recording' : 'Play your recording'}
                            >
                              {isPlaying
                                ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 14 })
                                : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 14 })}
                            </button>
                            <span className="review-blob-size">{(blob.size / 1024).toFixed(1)} KB</span>
                          </div>
                          <div className="review-reveal-group">
                            <button
                              type="button"
                              className="review-reveal-toggle"
                              aria-expanded={expand.showCaption}
                              onClick={() => patchReviewRowExpand(audioIndex, { showCaption: !expand.showCaption })}
                            >
                              {expand.showCaption ? 'Hide caption' : 'Show caption'}
                            </button>
                            {expand.showCaption ? (
                              <div className="review-collapsible-body">
                                <div className="review-caption-line">
                                  <strong>Your caption:</strong>{' '}
                                  <span
                                    className={
                                      captionReviewDisplay.muted ? 'review-caption-muted' : 'review-caption-text'
                                    }
                                  >
                                    {captionReviewDisplay.main}
                                  </span>
                                </div>
                                {'note' in captionReviewDisplay && captionReviewDisplay.note ? (
                                  <p className="review-caption-note">{captionReviewDisplay.note}</p>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          {expectedTranslations.length > 0 ? (
                            <div className="review-expected-block">
                              {expand.expectedRevealed === 0 ? (
                                <button
                                  type="button"
                                  className="review-reveal-toggle"
                                  onClick={() => patchReviewRowExpand(audioIndex, { expectedRevealed: 1 })}
                                >
                                  Show expected
                                </button>
                              ) : (
                                <>
                                  <div className="review-expected-label">
                                    Expected
                                    {expectedTranslations.length > 1 ? (
                                      <span className="review-expected-step">
                                        {' '}
                                        ({expand.expectedRevealed} of {expectedTranslations.length})
                                      </span>
                                    ) : null}
                                  </div>
                                  {expand.expectedRevealed > 0 &&
                                  expand.expectedRevealed <= expectedTranslations.length ? (
                                    <div className="review-expected-item">
                                      {expectedTranslations[expand.expectedRevealed - 1]}
                                    </div>
                                  ) : null}
                                  {showPrevExpectedControl || showMoreExpectedControl ? (
                                    <div className="review-expected-nav">
                                      {showPrevExpectedControl ? (
                                        <button
                                          type="button"
                                          className="review-reveal-toggle review-prev-idea"
                                          onClick={() =>
                                            patchReviewRowExpand(audioIndex, {
                                              expectedRevealed: expand.expectedRevealed - 1,
                                            })
                                          }
                                        >
                                          Previous idea
                                        </button>
                                      ) : null}
                                      {showMoreExpectedControl ? (
                                        <button
                                          type="button"
                                          className="review-reveal-toggle review-more-idea"
                                          onClick={() =>
                                            patchReviewRowExpand(audioIndex, {
                                              expectedRevealed: Math.min(
                                                expand.expectedRevealed + 1,
                                                expectedTranslations.length
                                              ),
                                            })
                                          }
                                        >
                                          More idea
                                        </button>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="review-dialog-summary">
                <span className="review-dialog-summary-text">
                  Reviewed: {reviewedRecordings.size} / {totalAudios}
                </span>
                {reviewedRecordings.size === totalAudios ? (
                  <span className="review-dialog-summary-done">
                    {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                    All reviewed
                  </span>
                ) : null}
              </div>
            </div>

            <div className="review-dialog-actions">
              <button type="button" className="review-dialog-btn review-dialog-btn--cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="review-dialog-btn review-dialog-btn--confirm"
                onClick={confirmSubmit}
                disabled={reviewedRecordings.size !== totalAudios}
              >
                {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 18 })}
                <span>{isMockTestSubmission ? 'Confirm & submit' : 'Confirm practice'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentPage;
